import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LOCALES } from "@heylife/i18n";
import { contrastInk, isHexColor } from "@heylife/ui-tokens";
import type { Localized } from "./api";
import { friendlyAdminError } from "./apiError";
import { LegalRichEditor, readLegalRichMarkdown } from "./LegalRichEditor";
import { useT } from "./i18n";

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="lbl">{label}</span>
      {children}
      {hint ? <span className="muted hint">{hint}</span> : null}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} />;
}

export type LocalizedRichFieldHandle = { read: () => Localized };

/** de/en rich-text pair (legal pages); same layout as LocalizedField. */
export const LocalizedRichField = forwardRef<
  LocalizedRichFieldHandle,
  { label?: string; value: Localized | null | undefined; onChange: (v: Localized) => void }
>(function LocalizedRichField({ label, value, onChange }, ref) {
  const { t } = useT();
  const v = value ?? {};
  const deRef = useRef<HTMLDivElement | null>(null);
  const enRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => ({
    read: () => ({
      de: readLegalRichMarkdown(deRef.current?.innerHTML ?? ""),
      en: readLegalRichMarkdown(enRef.current?.innerHTML ?? ""),
    }),
  }));

  return (
    <div className="field">
      {label ? <span className="lbl">{label}</span> : null}
      <div className="pair legal-rich-pair">
        {LOCALES.map((loc) => (
          <div key={loc} className="legal-rich-col">
            <span className="eyebrow">{t(`admin.common.${loc}`)}</span>
            <LegalRichEditor
              editorRef={loc === "de" ? deRef : enRef}
              value={v[loc] ?? ""}
              onChange={(text) => onChange({ ...v, [loc]: text })}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

/** de/en pair for jsonb localized columns. */
export function LocalizedField({
  label,
  value,
  onChange,
  multiline,
  required,
}: {
  label: string;
  value: Localized | null | undefined;
  onChange: (v: Localized) => void;
  multiline?: boolean;
  required?: boolean;
}) {
  const { t } = useT();
  const v = value ?? {};
  return (
    <div className="field">
      <span className="lbl">
        {label}
        {required ? (
          <abbr className="req" title={t("admin.common.required")}>
            *
          </abbr>
        ) : null}
      </span>
      <div className="pair">
        {LOCALES.map((loc) => {
          const common = {
            value: v[loc] ?? "",
            placeholder: t(`admin.common.${loc}`),
            required: Boolean(required),
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...v, [loc]: e.target.value }),
          };
          return multiline ? <textarea key={loc} rows={3} {...common} /> : <input key={loc} {...common} />;
        })}
      </div>
    </div>
  );
}

export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function Msg({ msg }: { msg: { kind: "error" | "success"; text: string } | null }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!msg) {
      setOpen(false);
      return;
    }
    setOpen(true);
    const id = window.setTimeout(() => setOpen(false), msg.kind === "error" ? 6400 : 3200);
    return () => window.clearTimeout(id);
  }, [msg]);
  if (!msg || !open) return null;
  return createPortal(
    <div className={`toast ${msg.kind}`} role={msg.kind === "error" ? "alert" : "status"}>
      <span className="toast-text">{msg.text}</span>
      <button type="button" className="toast-close" onClick={() => setOpen(false)} aria-label={t("admin.common.close")}>
        ×
      </button>
    </div>,
    document.body,
  );
}

export type Flash = { kind: "error" | "success"; text: string } | null;

/** Runs an async action and surfaces the result as a toast. */
export function useFlash(): [Flash, (fn: () => Promise<unknown>, okText?: string) => Promise<boolean>, boolean] {
  const { t } = useT();
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  const show = (next: Exclude<Flash, null>) => {
    clearTimer();
    setFlash(next);
    timer.current = setTimeout(() => setFlash(null), next.kind === "error" ? 6400 : 3200);
  };
  const run = async (fn: () => Promise<unknown>, okText?: string) => {
    setBusy(true);
    clearTimer();
    setFlash(null);
    try {
      await fn();
      if (okText) show({ kind: "success", text: okText });
      return true;
    } catch (e) {
      show({ kind: "error", text: friendlyAdminError(t, e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [flash, run, busy];
}

export function SlugInput({ value, onValue, onBlurValue }: { value: string; onValue: (value: string) => void; onBlurValue: () => void }) {
  return (
    <input
      required
      maxLength={40}
      spellCheck={false}
      autoCapitalize="none"
      autoCorrect="off"
      value={value}
      onChange={(e) => onValue(e.target.value)}
      onBlur={onBlurValue}
    />
  );
}

export function ConfirmDelete({ onConfirm, label }: { onConfirm: () => void; label?: string }) {
  const { t } = useT();
  const [arm, setArm] = useState(false);
  if (!arm)
    return (
      <button type="button" className="btn small danger ghost" onClick={() => setArm(true)}>
        {label ?? t("admin.common.delete")}
      </button>
    );
  return (
    <span className="confirm">
      <span className="muted">{t("admin.common.confirmDelete")}</span>
      <button type="button" className="btn small danger" onClick={onConfirm}>
        {t("admin.common.yes")}
      </button>
      <button type="button" className="btn small ghost" onClick={() => setArm(false)}>
        {t("admin.common.no")}
      </button>
    </span>
  );
}

export function splitLines(s: string): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
}

export const THEMES = ["carmine", "corral", "yellow", "pine", "indigo", "violet", "beige", "grey"] as const;

export const THEME_DETAILS: Record<string, { label: string; color: string; ink: string }> = {
  carmine: { label: "Carmine", color: "#E8124A", ink: "#FFFFFF" },
  corral: { label: "Corral", color: "#FF755D", ink: "#FFFFFF" },
  yellow: { label: "Yellow", color: "#FF982B", ink: "#0B1622" },
  pine: { label: "Pine", color: "#004A5B", ink: "#FFFFFF" },
  indigo: { label: "Indigo", color: "#133666", ink: "#FFFFFF" },
  violet: { label: "Violet", color: "#6D3376", ink: "#FFFFFF" },
  beige: { label: "Beige", color: "#E4C7C2", ink: "#0B1622" },
  grey: { label: "Grey", color: "#BFC6C9", ink: "#0B1622" },
};

export function ColorPicker({ value, onChange }: { value?: string; onChange: (theme: string) => void }) {
  const { t } = useT();
  const current = value || "indigo";
  const isCustom = !THEMES.includes(current as (typeof THEMES)[number]);
  const customColor = isHexColor(current) ? current : "#133666";
  const currentDetail = isCustom
    ? { label: t("admin.content.customColor"), color: customColor, ink: contrastInk(customColor) }
    : THEME_DETAILS[current] ?? { label: current, color: customColor, ink: "#FFFFFF" };
  const [hexDraft, setHexDraft] = useState(customColor);
  useEffect(() => {
    if (isHexColor(current)) setHexDraft(current);
  }, [current]);

  return (
    <div className="color-picker-wrap">
      <div className="color-picker-swatches" role="radiogroup" aria-label="Theme color">
        {THEMES.map((th) => {
          const detail = THEME_DETAILS[th]!;
          const isSelected = current === th;
          return (
            <button
              key={th}
              type="button"
              className={`color-swatch ${isSelected ? "selected" : ""}`}
              style={{ backgroundColor: detail.color }}
              title={detail.label}
              onClick={() => onChange(th)}
              aria-checked={isSelected}
              role="radio"
            >
              {isSelected ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={detail.ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : null}
            </button>
          );
        })}

        {/* 9th swatch: Custom Color Picker */}
        <label
          className={`color-swatch color-swatch-custom ${isCustom ? "selected" : ""}`}
          style={isCustom ? { backgroundColor: currentDetail.color } : undefined}
          title={isCustom ? `${t("admin.content.customColor")} (${current})` : t("admin.content.customColor")}
          role="radio"
          aria-checked={isCustom}
        >
          <input
            type="color"
            value={isHexColor(current) ? current.toLowerCase() : "#133666"}
            onChange={(e) => onChange(e.target.value)}
            className="color-native-input"
            aria-label={t("admin.content.customColor")}
          />
          {isCustom ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={currentDetail.ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}
            >
              <circle cx="13.5" cy="6.5" r=".5" fill="#FFFFFF" />
              <circle cx="17.5" cy="10.5" r=".5" fill="#FFFFFF" />
              <circle cx="8.5" cy="7.5" r=".5" fill="#FFFFFF" />
              <circle cx="6.5" cy="12.5" r=".5" fill="#FFFFFF" />
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
            </svg>
          )}
        </label>
      </div>
      <div className="color-picker-preview">
        <span className="color-dot" style={{ backgroundColor: currentDetail.color }} />
        <span className="color-label">{currentDetail.label}</span>
        {isCustom ? (
          <input
            type="text"
            className="color-hex-input"
            value={hexDraft}
            placeholder="#000000"
            maxLength={7}
            onChange={(e) => {
              const raw = e.target.value.trim();
              const next = raw === "" || raw.startsWith("#") ? raw : `#${raw}`;
              setHexDraft(next);
              if (isHexColor(next)) onChange(next);
            }}
            title="Hex color"
          />
        ) : null}
      </div>
    </div>
  );
}

export function GripIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={`drag-handle-icon ${className}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="5" r="1" fill="currentColor" />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="19" r="1" fill="currentColor" />
      <circle cx="15" cy="5" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="19" r="1" fill="currentColor" />
    </svg>
  );
}
